import { useCallback, useMemo, useRef, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import type { EmailChoices } from "@pushback-and-chaos/contracts";
import { ProblemJsonError, type PushbackClient } from "@pushback-and-chaos/client";
import type { EmailChoicesFor } from "../screens/email-choices.js";
import { EMAIL_CHOICES } from "../design/copy.js";
import { retryDelay, retryTransient } from "./retry.js";

/**
 * EMAIL CHOICES (2026-10-07): the server's saved set, read once for the desk
 * card and settings, and the one save both of them call. Nothing until the
 * first Harborlight report, which the server decides; `openingKey` re-reads it
 * whenever the coach's opening changes, which is when a report lands.
 *
 * One save at a time across both surfaces (Codex's review): both forms'
 * controls are disabled while one is pending, and a second is refused. A
 * refusal is said on the form that saved: the server's 404/409 words, else
 * the failed-save line. A save that lands writes the server's answer into the
 * shared cache before the forms unlock (Codex's second review), so the other
 * form holds the new set even when the re-read after it fails.
 */
export function useEmailChoices(
  client: Pick<PushbackClient, "getEmailChoices" | "putEmailChoices">,
  guestId: string | null,
  openingKey: string | null,
): { emailChoices: EmailChoicesFor | null; emailBusy: boolean; saveEmailChoices: (choices: EmailChoices) => Promise<void> } {
  const queryClient = useQueryClient();
  const queryKey = useMemo(() => ["email-choices", guestId, openingKey], [guestId, openingKey]);
  const query = useQuery({
    queryKey,
    enabled: guestId !== null,
    queryFn: () => client.getEmailChoices(),
    retry: retryTransient,
    retryDelay,
  });
  const state = query.data;
  const emailChoices: EmailChoicesFor | null = state === undefined || !state.eligible ? null : state.signedIn ? { signedIn: true, addressVerified: state.addressVerified, choices: state.choices } : { signedIn: false };
  const [emailBusy, setEmailBusy] = useState(false);
  const saving = useRef(false);
  const saveEmailChoices = useCallback(
    async (choices: EmailChoices) => {
      if (saving.current) throw new Error(EMAIL_CHOICES.notSaved);
      saving.current = true;
      setEmailBusy(true);
      try {
        queryClient.setQueryData(queryKey, await client.putEmailChoices(choices));
      } catch (caught) {
        throw new Error(caught instanceof ProblemJsonError && (caught.status === 404 || caught.status === 409) ? caught.message : EMAIL_CHOICES.notSaved);
      } finally {
        // A failed re-read keeps the answer just written; react-query keeps data on error.
        await queryClient.invalidateQueries({ queryKey: ["email-choices"] });
        saving.current = false;
        setEmailBusy(false);
      }
    },
    [client, queryClient, queryKey],
  );
  return { emailChoices, emailBusy, saveEmailChoices };
}
